import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { clubService, eventService } from '../services/apiClient';
import Navbar from '../components/Navbar';
import EventCard from '../components/EventCard';

const StudentDashboard = () => {
  const { user } = useContext(AuthContext);
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stats, setStats] = useState({
    clubsJoined: 0,
    eventsRegistered: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchClubs();
    fetchEvents();
    loadUserStats();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await clubService.getAllClubs({
        search: searchTerm,
        category: categoryFilter,
      });
      setClubs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await eventService.getAllEvents();
      const now = new Date();
      const upcomingEvents = (response.data.events || [])
        .filter(e => new Date(e.date) > now)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const loadUserStats = () => {
    if (user?.clubsJoined) {
      setStats(prev => ({
        ...prev,
        clubsJoined: user.clubsJoined.length || 0,
      }));
    }
    if (user?.eventsRegistered) {
      setStats(prev => ({
        ...prev,
        eventsRegistered: user.eventsRegistered.length || 0,
      }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClubs();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, categoryFilter]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-gray-600 text-sm font-semibold">Clubs Joined</h3>
            <p className="text-3xl font-bold text-purple-600">{stats.clubsJoined}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-gray-600 text-sm font-semibold">Events Registered</h3>
            <p className="text-3xl font-bold text-indigo-600">{stats.eventsRegistered}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-gray-600 text-sm font-semibold">Upcoming Events</h3>
            <p className="text-3xl font-bold text-blue-600">{events.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-gray-600 text-sm font-semibold">Active Memberships</h3>
            <p className="text-3xl font-bold text-green-600">0</p>
          </div>
        </div>

        {/* Upcoming Events Section */}
        <div className="mb-16">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight">Upcoming Events</h2>
              <p className="text-gray-500 font-medium">Don't miss out on these opportunities!</p>
            </div>
            <button
              onClick={() => navigate('/events')}
              className="text-purple-600 font-bold text-sm hover:text-purple-700 transition flex items-center gap-1"
            >
              View Calendar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.length > 0 ? (
              events.map((event) => (
                <EventCard key={event._id} event={event} />
              ))
            ) : (
              <div className="col-span-full bg-white p-12 rounded-3xl border-2 border-dashed border-gray-200 text-center">
                <p className="text-gray-400 font-medium">No upcoming events found. Check back soon!</p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-12">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchClubs();
            }}
            className="flex gap-4 mb-6"
          >
            <input
              type="text"
              placeholder="Search clubs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
            >
              <option value="">All Categories</option>
              <option value="Technical & Professional">Technical & Professional</option>
              <option value="Cultural & Literary">Cultural & Literary</option>
              <option value="Social Service">Social Service</option>
            </select>
          </form>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Clubs</h2>
          <div className="space-y-12">
            {[
              { id: 'tech', label: '💻 Technical & Professional Clubs', category: 'Technical & Professional' },
              { id: 'cultural', label: '🎭 Cultural & Literary Clubs', category: 'Cultural & Literary' },
              { id: 'social', label: '🤝 Social Service Clubs', category: 'Social Service' }
            ].map((section) => {
              const categoryClubs = clubs.filter(c => c.category === section.category);
              if (categoryClubs.length === 0 && categoryFilter !== '' && categoryFilter !== section.category) return null;
              if (categoryFilter !== '' && categoryFilter !== section.category) return null;

              return (
                <div key={section.id}>
                  <h3 className="text-xl font-semibold text-purple-700 mb-4 pb-2 border-b-2 border-purple-100 flex items-center">
                    {section.label}
                    <span className="ml-3 text-sm font-normal text-gray-400">({categoryClubs.length})</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryClubs.map((club) => (
                      <div key={club._id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all border-l-4 border-purple-500 hover:scale-[1.02]">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">{club.name}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{club.description}</p>
                        <div className="flex justify-between text-sm text-gray-500 mb-4">
                          <span>Members: {club.memberCount || 0}</span>
                          <span>Events: {club.upcomingEventsCount || 0}</span>
                        </div>
                        <button
                          onClick={() => navigate(`/club/${club._id}`)}
                          className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition-colors shadow-sm"
                        >
                          View Club
                        </button>
                      </div>
                    ))}
                    {categoryClubs.length === 0 && (
                      <p className="text-gray-400 italic">No clubs found in this category.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div >
  );
};

export default StudentDashboard;
